// Edge-of-list loader for paginated lists: asks for the next page as soon as
// that edge comes near. Its height is reserved so the list does not jump under
// the visitor while a page arrives.
//
// `root` scopes the observer to a scroll container — without it the viewport
// is the reference, and a list scrolling inside a modal would ask for every
// page at once.

import { useEffect, useRef } from 'react';
import ClipLoader from 'react-spinners/ClipLoader';
import './LoadMoreSentinel.scss';

export default function LoadMoreSentinel({
  hasMore,
  isLoading,
  onLoadMore,
  root = null,
  rootMargin = '400px',
}) {
  const ref = useRef(null);
  // The handler closes over the render that created it, and the observer must
  // not be rebuilt every time the list grows
  const load = useRef(onLoadMore);
  load.current = onLoadMore;

  // Re-observed once loading ends: a sentinel that stayed in view fires no new
  // intersection of its own, and a tall viewport would stop after one page
  useEffect(() => {
    const node = ref.current;
    if (!node || !hasMore || isLoading) return;

    // Ahead of the viewport, so the next page is in flight before the visitor
    // reaches the end of the current one
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && load.current(),
      { root, rootMargin },
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, [hasMore, isLoading, root, rootMargin]);

  if (!hasMore) return null;

  return (
    <div className='load-more' ref={ref}>
      <ClipLoader size={24} color='#3f90dc' loading={isLoading} />
    </div>
  );
}
